/**
 * Sektion „Anfos & Umsatz" — Übersicht: Kapitel, Datenstand und die Regeln, nach denen jede Zahl
 * der Sektion zählt. Leser sind Marketing und Geschäftsführung, deshalb Klartext statt Technik;
 * die technischen Erkennungsmerkmale der Kanäle stehen nur eingeklappt.
 * Plan: .claude/plans/2026-10-06-anfos-umsatz-sektion.md (P0).
 *
 * Persona-Umbau D6 (28.10.2026): jeder Kapitel-Teaser trägt eine Kernzahl mit Richtung und Fenster (`kernzahlen.ts`),
 * geladen aus bestehenden Endpunkten — je Teaser für sich, ein Ladefehler lässt nur diesen Teaser ohne Zahl.
 */
import { FC, ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ChapterPage, { useAuswertungenMeta } from '../../components/auswertungen/ChapterPage';
import ChartCard from '../../components/auswertungen/ChartCard';
import { BefundZeile, useLaden } from '../../components/auswertungen/kennzahlTeile';
import Explainer, { ExplainerSection } from '../../components/Explainer';
import { ATTRIBUTION, HEBEL_FRAGE, LEITFRAGE_UMSATZ, LEITFRAGEN, RECHENWEG_TESTS, TERMS } from '../../helpers/kpiDefinitions';
import {
  AuswertungenDefinitionen,
  fetchAuswertungenDefinitionen,
  fetchKanalVerlauf,
  fetchKundentypP2,
  fetchWebinarRankingGeteilt,
  formatBerlinWallclock,
} from '../../helpers/auswertungenData';
import { fetchBestellungen } from '../../helpers/bestellungenData';
import { fetchNachfass } from '../../helpers/hebelData';
import { fetchRueckblick } from '../../helpers/rueckblickData';
import { kachelDelta, vergleichEtikett } from './bestellungen/kachelVergleich';
import KopfKacheln from './umsatz/KopfKacheln';
import { germanNow } from '../../helpers/germanTime';
import { formatNumber } from '../../helpers/numberUtils';
import { nichtGezaehltText } from './nichtGezaehlt';
import {
  bestellungenKern,
  bestellungenLink,
  hebelKern,
  kanaeleKern,
  Kernzahl,
  lageBefund,
  letzteKw,
  umsatzKern,
  webinareKern,
} from './kernzahlen';

/** Welche Kernzahl ein Teaser trägt (D6); Werkzeuge ohne Zahl. */
type KernSchluessel = 'bestellungen' | 'webinare' | 'umsatz' | 'kanaele' | 'hebel';

const KAPITEL: { title: string; question: string; to?: string; kern?: KernSchluessel }[] = [
  // P5-Review #18: Bestellungen zuerst (Wochenblick der GF), Werkzeuge zuletzt; fehlten bis 27.10. in der Liste.
  {
    title: 'Bestellungen',
    to: '/auswertungen/bestellungen',
    question: 'Wie viele Bestellungen, Anfos und Verweigerungen kamen rein, und warum wurde verweigert?',
    kern: 'bestellungen',
  },
  {
    title: 'Webinare & Produkte',
    to: '/auswertungen/webinare-produkte',
    // E7.7 (P1-V3 § 2.1): dieselbe Leitfrage wie die Kapitelseite — eine Konstante.
    question: LEITFRAGEN.webinareProdukte,
    kern: 'webinare',
  },
  {
    title: 'Umsatz & Bezahlqualität',
    to: '/auswertungen/umsatz',
    // Dieselbe Leitfrage wie die Kapitelseite (P2-V3 §2.1) — eine Konstante.
    question: LEITFRAGE_UMSATZ,
    kern: 'umsatz',
  },
  {
    title: 'Kanäle & Werbeflächen',
    to: '/auswertungen/kanaele',
    // Wortgleich mit der Leitfrage der Kapitelseite (kanaele/index.tsx); „je Kontakt" las sich als „je Person" (vorschlag_v3 §4.1).
    question: LEITFRAGEN.kanaele,
    kern: 'kanaele',
  },
  {
    title: 'Hebel',
    to: '/auswertungen/hebel',
    // Wortgleich mit der Leitfrage der Kapitelseite (hebel/index.tsx, HEBEL_FRAGE); P4 schlank: Wellen, Nachfass, Cross-Sell.
    question: HEBEL_FRAGE,
    kern: 'hebel',
  },
  {
    title: 'Werkzeuge (Pflege, kein Bericht)',
    to: '/auswertungen/werkzeuge',
    question: TERMS.werkzeuge.tip,
  },
];

// P5-Review #7: „Bestellungen" und „verweigert" gehören neben „Anfos" — ohne sie las die GF Anfo = jede Bestellung.
const ZAEHLREGELN: (keyof typeof TERMS)[] = [
  'anfos',
  'bestellungenAlle',
  'verweigert',
  'vorlaeufig',
  'nichtGezaehlt',
  'zuordnungOhneKennung',
  'umsatzNetto',
  'anfoWertNetto',
  'wertBeiBestellung',
  'hochrechnung',
  'bezahlquote',
  'reife',
  'rechnungsstufe',
  'erstbesteller',
  'frueherGezahlt',
  'frueherNieGezahlt',
  'seriellerNichtzahler',
  'upsellQuote',
  'listenpreis',
  'kanal',
];

const EXPLAINER: ExplainerSection[] = [
  // Audit 30.10. C4: Testjargon steht nur noch hier, nicht mehr in den Tooltips der Karten.
  { label: 'Tests (▲/▼)', body: RECHENWEG_TESTS },
  {
    label: 'Datengrundlage',
    body: 'Bestellungen und Zahlungen aus PL-X (Mandant Finanztrends). PL-X liefert zweimal täglich (gegen 7 und 14 Uhr); die Zahlungsdaten werden alle sechs Stunden neu eingelesen. Bestellungen, die PL-X noch nicht vervollständigt hat, ergänzt der PX-Data-Export vorläufig — sie zählen als Anfo, aber in keiner Quote.',
  },
  {
    label: 'Berechnung',
    body: 'Jede Zahl der Sektion geht von derselben Liste aus: alle nicht stornierten Bestellungen ohne Testbestellungen der Firmenadresse. Kundentyp, Kanal und Rechnungsstufe werden einmal zentral bestimmt, nicht je Seite neu.',
  },
  {
    label: 'Aussage',
    // B44 (Persona-Check 26.10.2026): an die Sektionsregel angeglichen — Zeiträume über den Erwarteten Wert vergleichen,
    // „Bisher bezahlt" nur mit Zahlern und Ø Alter; Namen wie in den Kapiteln (E7).
    body: `„${TERMS.umsatzNetto.label}“ ist, was die Anfos bis heute wirklich gezahlt haben; es wächst mit ihrem Alter und steht deshalb immer mit bezahlten Anfos und Ø Alter. Der „${TERMS.wertBeiBestellung.label}“ ist die Erwartung für 36 Monate und hängt nicht vom Alter ab. Verschieden alte Zeiträume deshalb über den Erwarteten Wert vergleichen, nicht über „bezahlt“.`,
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Junge Zeiträume sind nie fertig: Rechnungen kommen erst nach der Testphase und dann quartalsweise. Ein Monat mit wenig bezahltem Umsatz ist deshalb oft nur jung, nicht schlecht.',
  },
];

const gross = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ─── Kernzahlen der Teaser (D6) ─────────────────────────────────────────────────────────────

/** Anfos der letzten KW: EIN Abruf der Seite „Bestellungen" — Zahl und Vergleich wie dort die Kachel bei „Letzte KW". */
const fetchBestellWoche = async (): Promise<Kernzahl> => {
  const w = letzteKw(germanNow().format('YYYY-MM-DD'));
  const kw = await fetchBestellungen(w.von, w.bis);
  const anfos = kw.uebersichten.produkt.total.anfos;
  const v = kw.vergleich;
  const d = v ? kachelDelta(anfos, v.anfos, v.zeitraeume, v.marke_ab) : null;
  return bestellungenKern(anfos, v && d ? { ...d, etikett: vergleichEtikett(v), schnitt: v.anfos.schnitt } : null, w);
};
/** Kanäle-Teaser (B3): die Karte „Versände der letzten Woche" mit ihrer Standardwoche (letzte volle KW). */
const fetchRueckblickLetzteWoche = () => fetchRueckblick();
/** Hebel-Teaser (B3): die Nachfass-Antwort im Fenster des Hebel-Kopfs (6 Monate). */
const fetchNachfass6 = () => fetchNachfass(6);

/** Kernzahl aus geladenen Daten; unerwartete Form (älteres Backend) → keine Zahl statt Absturz. */
function kernAus<T, R = Kernzahl>(d: T | null, f: (x: T) => R | null): R | null {
  if (!d) return null;
  try {
    return f(d);
  } catch {
    return null;
  }
}

const PFEIL_FARBE: Record<'▲' | '▼', string> = { '▲': '#2e7d32', '▼': '#c62828' };

/** Kernzahl im Teaser: Zahl groß mit Pfeil, darunter klein was sie ist (Fenster, Vergleich). Ohne Zahl: nichts. */
const KernZeile: FC<{ kern: Kernzahl | null | undefined }> = ({ kern }) =>
  kern ? (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography component="span" sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
          {kern.wert}
        </Typography>
        {kern.pfeil && (
          <Box component="span" aria-label={kern.pfeil === '▲' ? 'höher' : 'niedriger'} sx={{ fontSize: 16, color: PFEIL_FARBE[kern.pfeil] }}>
            {kern.pfeil}
          </Box>
        )}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12, mt: 0.25 }}>
        {kern.text}
      </Typography>
    </Box>
  ) : null;

/**
 * Erklär-Karte ohne Daten (Audit 30.10.2026 E3): startet zugeklappt — Titel, „So liest du das" und Quelle bleiben stehen,
 * der Inhalt kommt auf Klick. `weitere`: zusätzliche Aktionen, nur aufgeklappt sichtbar.
 */
const KlappKarte: FC<{ title: string; howToRead: string; source: string; kopf?: ReactNode; weitere?: ReactNode; children: ReactNode }> = ({
  title,
  howToRead,
  source,
  kopf,
  weitere,
  children,
}) => {
  const [offen, setOffen] = useState(false);
  return (
    <ChartCard
      title={title}
      howToRead={howToRead}
      source={source}
      actions={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {offen && weitere}
          <Link component="button" variant="body2" aria-expanded={offen} onClick={() => setOffen((v) => !v)}>
            {offen ? 'Ausblenden' : 'Zeigen'}
          </Link>
        </Box>
      }
    >
      {kopf}
      <Collapse in={offen} unmountOnExit>
        {children}
      </Collapse>
    </ChartCard>
  );
};

/** Ein Teaser der Zeile „Diese Woche" (E2): Titel als Link, darunter die Kernzahl (ohne Zahl nur der Titel). */
const WochenTeaser: FC<{ titel: string; to: string; kern: Kernzahl | null }> = ({ titel, to, kern }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '6px', p: 2 }}>
    <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
      <Link component={RouterLink} to={to} underline="hover" color="inherit">
        {titel}
      </Link>
    </Typography>
    <KernZeile kern={kern} />
  </Box>
);

const DefinitionList: FC<{ items: { label: string; tip: string }[] }> = ({ items }) => (
  // Ab md zweispaltig (Begriff | Text). Darunter je Eintrag ein Block: Begriff eng an seinem Text,
  // deutlicher Abstand zum nächsten Eintrag — sonst ist nicht zu sehen, was zusammengehört.
  <Box
    component="dl"
    sx={{
      m: 0,
      display: { xs: 'block', md: 'grid' },
      gridTemplateColumns: '220px 1fr',
      rowGap: 1.5,
      columnGap: 3,
    }}
  >
    {items.map((i) => (
      <Box key={i.label} sx={{ display: { xs: 'block', md: 'contents' }, mb: { xs: 2, md: 0 } }}>
        <Typography component="dt" variant="body2" sx={{ fontWeight: 600, mb: { xs: 0.25, md: 0 } }}>
          {i.label}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0, lineHeight: 1.6, maxWidth: '68ch' }}>
          {i.tip}
        </Typography>
      </Box>
    ))}
  </Box>
);

const Bestand: FC = () => {
  const meta = useAuswertungenMeta();
  if (!meta) return null;
  return (
    <Typography variant="body1" sx={{ mb: 2 }}>
      Grundlage sind{' '}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {formatNumber(meta.population.anfos, 0)} Anfos
      </Box>{' '}
      seit {formatBerlinWallclock(meta.population.since)}
      {meta.population.provisional > 0 ? `, davon ${formatNumber(meta.population.provisional, 0)} vorläufig ergänzt` : ''}.
      {nichtGezaehltText(meta.population)}
    </Typography>
  );
};


const AuswertungenUebersicht: FC = () => {
  const [defs, setDefs] = useState<AuswertungenDefinitionen | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [technik, setTechnik] = useState(false);
  // Je Teaser ein eigener Abruf: ein Fehler lässt nur diesen Teaser ohne Zahl (D6).
  const bestellWoche = useLaden(fetchBestellWoche);
  const ranking = useLaden(fetchWebinarRankingGeteilt);
  const kundentyp = useLaden(fetchKundentypP2);
  const rueckblick = useLaden(fetchRueckblickLetzteWoche);
  const nachfass = useLaden(fetchNachfass6);
  const verlauf = useLaden(fetchKanalVerlauf);
  const kerne: Record<KernSchluessel, Kernzahl | null> = {
    bestellungen: bestellWoche.data,
    webinare: kernAus(ranking.data, webinareKern),
    umsatz: kernAus(kundentyp.data, umsatzKern),
    kanaele: kernAus(rueckblick.data, kanaeleKern),
    hebel: kernAus(nachfass.data, hebelKern),
  };
  // B4: der Bestellungen-Teaser öffnet genau die gezeigte KW.
  const bestellLink = bestellungenLink(letzteKw(germanNow().format('YYYY-MM-DD')));
  // C1: Befund der Lage aus dem Monatsverlauf; Ladefehler/fremde Form → keine Zeile (fail-soft wie die Teaser).
  const lage = kernAus(verlauf.data, lageBefund);

  useEffect(() => {
    let cancelled = false;
    fetchAuswertungenDefinitionen()
      .then((d) => {
        if (!cancelled) setDefs(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setFehler(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ChapterPage
      title="Anfos & Umsatz"
      question="Übersicht: was die Zahlen dieser Sektion zählen und wie aktuell sie sind"
      intro="Hier stehen die Auswertungen aus dem Anfo-Workshop vom 23.09.2026 dauerhaft und laufend aktualisiert. Alle Kapitel zählen nach denselben Regeln — sie sind auf dieser Seite beschrieben."
    >
      <Explainer title="Wie diese Sektion rechnet" sections={EXPLAINER} />

      {/* Runde 2 (GF): „besser oder schlechter?" gehört nach oben — vorher standen hier nur zwei grüne ▲ (Woche,
          Prognose), während das Quartal −51 % lief. Dieselben drei Kacheln wie oben auf „Umsatz & Bezahlqualität". */}
      <ChartCard
        title="Lage"
        howToRead="Die drei Zahlen, an denen man sieht, ob es besser oder schlechter läuft — dieselben wie oben im Kapitel „Umsatz & Bezahlqualität“, jede mit ihrem Fenster."
        source="Kapitel Umsatz & Bezahlqualität (Kundentyp, Jahresvergleich, nächste Rechnungen); Befund aus dem Monatsverlauf der Kanäle"
      >
        {/* C1: Anfos je Tag im laufenden Monat gegen die letzten drei vollen Monate und den Vorjahresmonat. */}
        {lage && <BefundZeile text={'text' in lage ? lage.text : undefined} fehlt={'fehlt' in lage ? lage.fehlt : null} tip="Anfos einschließlich vorläufiger, wie im Monatsverlauf (Kapitel Kanäle). Der laufende Monat zählt bis zum Tag des PL-X-Datenstands, der Vorjahresmonat bis zum selben Tag." />}
        <KopfKacheln />
      </ChartCard>

      {/* E2: die Woche auf einen Blick — dieselben Zahlen wie die Kapitel-Teaser darunter, mit Link auf genau diese Woche. */}
      <ChartCard
        title="Diese Woche"
        howToRead="Was die letzte volle Kalenderwoche gebracht hat und was für den nächsten Sonntag empfohlen ist. Jede Zahl führt auf die Karte, aus der sie stammt."
        source="Bestellungen (letzte KW), Versände der letzten Woche (Kanäle), Empfehlung (Webinare & Produkte)"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
          <WochenTeaser titel="Bestellungen der letzten KW" to={bestellLink} kern={kerne.bestellungen} />
          <WochenTeaser titel="Bestell-Links der Woche" to="/auswertungen/kanaele#letzte-woche" kern={kerne.kanaele} />
          <WochenTeaser titel="Empfehlung für Sonntag" to="/auswertungen/webinare-produkte" kern={kerne.webinare} />
        </Box>
      </ChartCard>

      <ChartCard
        title="Kapitel"
        howToRead={`Je Kapitel die Hauptfrage und eine Kernzahl, wo möglich mit Richtung; jede Karte in den Kapiteln nennt ihre Quelle, ihren Stand und die Begriffe, die sie verwendet. ${TERMS.teaserKernzahl.label}: ${TERMS.teaserKernzahl.tip}`}
        source="Bestellungen, Empfehlung, Bezahlquote, Werbeflächen — dieselben Abfragen wie die Kapitel"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {KAPITEL.map((k) => (
            <Box key={k.title} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '6px', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {k.to ? (
                    <Link component={RouterLink} to={k.kern === 'bestellungen' ? bestellLink : k.to} underline="hover" color="inherit">
                      {k.title}
                    </Link>
                  ) : (
                    k.title
                  )}
                </Typography>
                {!k.to && <Chip label="folgt" size="small" variant="outlined" />}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {k.question}
              </Typography>
              {k.kern && <KernZeile kern={kerne[k.kern]} />}
            </Box>
          ))}
        </Box>
      </ChartCard>

      {/* E3: die drei Erklär-Karten tragen keine Daten und starten zugeklappt; die Grundlage (Zahl der Anfos) bleibt sichtbar. */}
      <KlappKarte
        title="Was wir zählen"
        howToRead="Diese Begriffe bedeuten in jedem Kapitel dasselbe. Steht eine Zahl ohne weiteren Hinweis da, gilt die Definition von hier."
        source="PL-X-Bestellungen und Zahlungen"
        kopf={<Bestand />}
      >
        {/* „Davon verweigert" ist ein Spaltenkopf; als Begriff steht das Wort selbst („Verweigert"). */}
        <DefinitionList
          items={ZAEHLREGELN.map((k) => ({ label: k === 'verweigert' ? gross(TERMS.verweigert.short) : TERMS[k].label, tip: TERMS[k].tip }))}
        />
      </KlappKarte>

      <KlappKarte
        title="Welche Bestellungen eine Zahl zählt"
        howToRead="Je nach Frage werden Bestellungen anders zugeordnet. Jede Karte nennt, welche Zuordnung sie verwendet. Zahlen mit verschiedener Zuordnung werden nie miteinander verrechnet."
        source="Zuordnungsregeln der Sektion"
      >
        <DefinitionList items={Object.values(ATTRIBUTION).map((a) => ({ label: a.label, tip: a.tip }))} />
      </KlappKarte>

      <KlappKarte
        title="Woran wir den Kanal erkennen"
        howToRead="Jede Bestellung trägt im Bestell-Link eine Kennung (Banner). Daran erkennen wir den Kanal; die Regeln werden von oben nach unten geprüft, die erste passende gilt."
        source="Kanalregeln, dieselben auf allen Seiten des Dashboards"
        weitere={
          <Link component="button" variant="body2" onClick={() => setTechnik((v) => !v)}>
            {technik ? 'Technische Regeln ausblenden' : 'Technische Regeln zeigen'}
          </Link>
        }
      >
        {fehler && <Alert severity="warning">Kanalregeln konnten nicht geladen werden: {fehler}</Alert>}
        {defs && (
          <Table size="small" sx={{ '& th, & td': { px: { xs: 1, md: 2 } } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: { md: 260 } }}>Kanal</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bedeutung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {defs.channels.map((c) => (
                <TableRow key={c.key}>
                  {/* P5-Review #9: der Hinweis „auf /plx-orders: …" ist entfallen — /plx-orders leitet seit P5 hierher um. */}
                  <TableCell sx={{ verticalAlign: 'top' }}>{c.label}</TableCell>
                  <TableCell>
                    {c.description}
                    <Collapse in={technik} unmountOnExit>
                      <Box
                        component="ul"
                        sx={{ m: 0, mt: 0.5, pl: 2, fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}
                      >
                        {c.rules.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </KlappKarte>
    </ChapterPage>
  );
};

export default AuswertungenUebersicht;
